import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import {
  type BaseProductDto,
  type GroupedProduct,
  type SortingProductsType,
} from "@/types/product/productTypes";
import { useProductsStore } from "@/stores/product/products/products";
import { Claim } from "@/common/claims/claimsEnum";
import type { TableColumnTypeCustom } from "@/types/claims";
import { useTableColumns } from "@/composables/table/tableColumns";

export type ColumnTypeCatalogTable = {
  title: string;
  dataIndex: keyof ProductTableDataType;
  width: number;
  fixed: string;
};

export type ProductTableDataType = BaseProductDto & {
  ozonColorArticle: string | null;
  action: string;
  linksToMarkets: string[];
};

export type ProductTableFilterType = {
  searchPhrase: string;
  createdInOzon: boolean | null;
  createdInWb: boolean | null;
  notCreatedInOzon?: boolean | null;
  notCreatedInWb?: boolean | null;
  outOfStockFBS: boolean | null;
  outOfStockFBO: boolean | null;
  readyToExport?: boolean | null;
  duplicate?: boolean | null;
  sortedField?: SortingProductsType | null | string;
  isOrderByDesc?: boolean;
  brands: string[];
  categories: string[];
};

export const initialFilter: ProductTableFilterType = {
  searchPhrase: "",
  createdInOzon: null,
  createdInWb: null,
  notCreatedInOzon: null,
  notCreatedInWb: null,
  outOfStockFBS: null,
  outOfStockFBO: null,
  readyToExport: null,
  duplicate: null,
  sortedField: null,
  isOrderByDesc: false,
  brands: [],
  categories: [],
};

export const useProductsTableStore = defineStore("productsTable", () => {
  const productStore = useProductsStore();

  const { productsUnitedByWBNomenclatureId } = storeToRefs(productStore);

  const filter = ref<ProductTableFilterType>({ ...initialFilter });

  const selectedRows = ref<GroupedProduct[]>([]);
  const selectedRowKeys = computed<string[]>(() => {
    return selectedRows.value.map((row) => row.id);
  });

  const filteredData = computed<GroupedProduct[]>(() => {
    return productStore.productsUnitedByWBNomenclatureId;
  });

  const columnFilters = computed(() => {
    const filters: { [key: string]: Array<{ text: string; value: string }> } =
      {};
    ["brand", "category"].forEach((field) => {
      const uniqueValues = new Set(
        filteredData.value.map(
          (product) => product[field as keyof BaseProductDto],
        ),
      );
      filters[field] = Array.from(uniqueValues).map((value) => ({
        text: value as string,
        value: value as string,
      }));
    });
    return filters;
  });

  const initInitializationColumns = () => {
    return [
      {
        title: "Фото",
        dataIndex: "titleImageLink",
        width: 50,
        fixed: "left",
        align: "center",
        resizable: true,
      },
      {
        title: "Название",
        dataIndex: "title",
        width: 200,
        fixed: "left",
        align: "center",
        resizable: true,
      },
      {
        title: "Размер",
        dataIndex: "size",
        width: 50,
        fixed: "left",
        align: "center",
        resizable: true,
      },
      {
        title: "Ссылки на МП",
        dataIndex: "linksToMarkets",
        width: 50,
        align: "center",
        resizable: true,
      },
      {
        title: "Штрих-код",
        dataIndex: "barcode",
        width: 150,
        resizable: true,
      },
      {
        title: "GTIN",
        dataIndex: "gtin",
        width: 150,
        align: "center",
        resizable: true,
      },
      {
        title: "Категория",
        dataIndex: "category",
        width: 100,
        resizable: true,
      },
      {
        title: "Бренд",
        dataIndex: "brand",
        width: 50,
        resizable: true,
      },
      {
        title: "Остатки FBS всего",
        dataIndex: "fbsResidues",
        width: 200,
        resizable: true,
      },
      {
        title: "Остатки FBO всего",
        dataIndex: "residueAllFBO",
        width: 200,
        resizable: true,
      },
      {
        title: "Остатки FBO WB",
        dataIndex: "wildberriesFBO",
        width: 200,
        resizable: true,
      },
      {
        title: "Остатки FBO Ozon",
        dataIndex: "ozonFBO",
        width: 200,
        resizable: true,
      },
      // {
      //   title: "Остатки FBO Я.Маркет",
      //   dataIndex: "yandexFBO",
      //   width: 200,
      //   resizable: true,
      // },
      // {
      //   title: "Артикул для объединения в одну карточку",
      //   dataIndex: "articleObedenenie",
      //   width: 230,
      //   resizable: true,
      // },
      {
        title: "Цвет Ozon",
        dataIndex: "ozonColorArticle",
        width: 200,
        resizable: true,
      },
      {
        title: "Дата создания на МП",
        dataIndex: "createdDate",
        width: 165,
        resizable: true,
      },
      {
        title: "Автор",
        dataIndex: "userId",
        width: 142,
        resizable: true,
      },
      {
        title: "Артикул продавца на МП",
        dataIndex: "wildberriesArticle",
        width: 200,
        resizable: true,
      },
      {
        title: "Удалить",
        dataIndex: "action",
        width: 50,
        fixed: "right",
        align: "center",
        resizable: true,
        claim: Claim.CanDeleteProducts,
      },
    ];
  };

  const columnForInit = ref<TableColumnTypeCustom[]>([
    ...initInitializationColumns(),
  ]);

  const columnsConfigName = "baskitCatalogTableCols1";

  const { columnsFiltered, dragList, initDragList } = useTableColumns(
    columnForInit,
    columnsConfigName,
    productsUnitedByWBNomenclatureId,
  );

  // initDragList();

  const clearFilter = () => {
    filter.value = { ...initialFilter };
  };

  const reset = () => {
    clearFilter();
    columnForInit.value.length = 0;
    columnForInit.value = [...initInitializationColumns()];
  };

  return {
    filteredData,
    filter,
    columnsFiltered,
    clearFilter,
    initDragList,
    dragList,
    columnForInit,
    columnsConfigName,
    selectedRowKeys,
    selectedRows,
    reset,
  };
});
